/**
 * 用户管理页面
 * 功能：用户的增删改查、搜索筛选
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  message,
  Popconfirm,
  Select,
  Card,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_BASE_URL } from '../../api/config';
import './UserList.css';

const { Option } = Select;

// 用户角色映射
const ROLE_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '普通用户', color: 'blue' },
  1: { text: '会员', color: 'gold' },
  3: { text: '推荐官', color: 'purple' },
};

interface UserType {
  id: number;
  nickname?: string;
  area_code?: string;
  phone?: string;
  email?: string;
  invite_code: string;
  parent_id: number;
  avatar_url?: string;
  language?: string;
  role: number;
  is_logout: boolean;
  is_banned: boolean;
  is_acceptor: boolean;
  created_at?: string;
  updated_at?: string;
}

const UserManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<UserType[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<string | null>(null);

  // API基础URL
  const API_BASE = `${API_BASE_URL}/users_app`;

  // 加载用户列表
  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      if (searchText) {
        params.append('keyword', searchText);
      }

      if (sortField && sortOrder) {
        params.append('sort_field', sortField);
        params.append('sort_order', sortOrder);
      }

      const response = await fetch(`${API_BASE}/users?${params}`);
      const data = await response.json();

      if (response.ok) {
        setDataSource(data.users);
        setTotal(data.total);
      } else {
        message.error('加载用户列表失败');
      }
    } catch (error) {
      message.error('网络错误');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, pageSize, searchText, sortField, sortOrder]);

  // 打开新增/编辑弹窗
  const showModal = (user?: UserType) => {
    if (user) {
      setEditingUser(user);
      form.setFieldsValue(user);
    } else {
      setEditingUser(null);
      form.resetFields();
      // 生成随机邀请码
      form.setFieldsValue({
        invite_code: `INV${Date.now().toString().slice(-8)}`,
        parent_id: 0,
        role: 0,
      });
    }
    setModalVisible(true);
  };

  // 保存用户
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const url = editingUser
        ? `${API_BASE}/users/${editingUser.id}`
        : `${API_BASE}/users`;

      const method = editingUser ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      if (response.ok) {
        message.success(editingUser ? '更新成功' : '添加成功');
        setModalVisible(false);
        form.resetFields();
        loadUsers();
      } else {
        const error = await response.json();
        message.error(error.detail || '操作失败');
      }
    } catch (error) {
      console.error('保存失败:', error);
    }
  };

  // 删除用户
  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE}/users/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        message.success('删除成功');
        loadUsers();
      } else {
        message.error('删除失败');
      }
    } catch (error) {
      message.error('网络错误');
    }
  };

  const columns: ColumnsType<UserType> = [
    {
      title: '用户ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      fixed: 'left',
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
      width: 120,
      render: (text) => text || '未设置',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 100,
      render: (role: number) => {
        const roleInfo = ROLE_MAP[role] || { text: '未知', color: 'default' };
        return <Tag color={roleInfo.color}>{roleInfo.text}</Tag>;
      },
    },
    {
      title: '状态',
      key: 'status',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {record.is_banned && <Tag color="red">已封禁</Tag>}
          {record.is_logout && <Tag color="default">已注销</Tag>}
          {record.is_acceptor && <Tag color="green">提现官</Tag>}
          {!record.is_banned && !record.is_logout && !record.is_acceptor && (
            <Tag color="success">正常</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      sorter: true,
      render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除这个用户吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="user-list-container">
      {/* 页面头部 */}
      <div className="page-header">
        <h1 className="page-title">👥 用户列表</h1>
        <Space>
          <Input
            placeholder="搜索昵称"
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            新增用户
          </Button>
        </Space>
      </div>

      {/* 用户表格 */}
      <Card bordered={false}>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1300 }}
          onChange={(pagination, filters, sorter: any) => {
            // 处理排序变化
            if (sorter.field) {
              setSortField(sorter.field);
              setSortOrder(sorter.order);
            } else {
              setSortField(null);
              setSortOrder(null);
            }
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个用户`,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            },
          }}
        />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingUser ? '编辑用户' : '新增用户'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={700}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="昵称"
            name="nickname"
            rules={[{ required: true, message: '请输入昵称' }]}
          >
            <Input placeholder="请输入昵称" />
          </Form.Item>

          <Space style={{ width: '100%' }} size="large">
            <Form.Item
              label="区号"
              name="area_code"
              style={{ width: 100 }}
            >
              <Input placeholder="+86" />
            </Form.Item>

            <Form.Item
              label="手机号"
              name="phone"
              style={{ flex: 1 }}
            >
              <Input placeholder="请输入手机号" />
            </Form.Item>
          </Space>

          <Form.Item
            label="邮箱"
            name="email"
            rules={[{ type: 'email', message: '邮箱格式不正确' }]}
          >
            <Input placeholder="请输入邮箱" />
          </Form.Item>

          <Form.Item
            label="邀请码"
            name="invite_code"
            rules={[{ required: true, message: '请输入邀请码' }]}
          >
            <Input placeholder="请输入邀请码" disabled={!!editingUser} />
          </Form.Item>

          <Form.Item
            label="推荐人ID"
            name="parent_id"
          >
            <Input type="number" placeholder="0表示无推荐人" />
          </Form.Item>

          <Form.Item
            label="用户角色"
            name="role"
            rules={[{ required: true, message: '请选择用户角色' }]}
          >
            <Select>
              <Option value={0}>普通用户</Option>
              <Option value={1}>会员</Option>
              <Option value={3}>推荐官</Option>
            </Select>
          </Form.Item>

          {editingUser && (
            <>
              <Form.Item
                label="是否封禁"
                name="is_banned"
                valuePropName="checked"
              >
                <Select>
                  <Option value={false}>否</Option>
                  <Option value={true}>是</Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="是否注销"
                name="is_logout"
                valuePropName="checked"
              >
                <Select>
                  <Option value={false}>否</Option>
                  <Option value={true}>是</Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="是否提现官"
                name="is_acceptor"
                valuePropName="checked"
              >
                <Select>
                  <Option value={false}>否</Option>
                  <Option value={true}>是</Option>
                </Select>
              </Form.Item>
            </>
          )}

          <Form.Item
            label="头像URL"
            name="avatar_url"
          >
            <Input placeholder="请输入头像URL" />
          </Form.Item>

          <Form.Item
            label="语言偏好"
            name="language"
          >
            <Select>
              <Option value="zh-CN">简体中文</Option>
              <Option value="zh-TW">繁体中文</Option>
              <Option value="en-US">English</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
